import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useIsFeatureEnabled } from "@/hooks/useIsFeatureEnabled"
import { toast } from "@/hooks/useToast"
import {
  genesysTelephonyCheckValidity,
  genesysTelephonyLoginUrl,
  isGenesysTelephonyLoggedMessage,
} from "@/lib/genesys-telephony-login"
type PendingGenesysLogin = {
  loginWindow: Window
  action: VoidFunction
}

const useGenesysTelephony = () => {
  const isGenesysCallEnabled = useIsFeatureEnabled("GENESYS_TELEPHONY")
  const { t } = useTranslation()
  const pendingGenesysLoginRef = useRef<PendingGenesysLogin | null>(null)
  const [isGenesysLoginInFlight, setIsGenesysLoginInFlight] = useState(false)

  useEffect(() => {
    if (!isGenesysCallEnabled) return

    const handleGenesysLoginMessage = (event: MessageEvent<{ action?: string } | null>) => {
      const pendingGenesysLogin = pendingGenesysLoginRef.current
      if (!pendingGenesysLogin || !isGenesysTelephonyLoggedMessage(event, pendingGenesysLogin.loginWindow)) return

      pendingGenesysLoginRef.current = null
      pendingGenesysLogin.loginWindow.close()
      toast({ description: t("telephony.genesys.loginSuccessfully") })
      pendingGenesysLogin.action()
    }

    window.addEventListener("message", handleGenesysLoginMessage)

    return () => {
      window.removeEventListener("message", handleGenesysLoginMessage)
    }
  }, [isGenesysCallEnabled, t])

  const openGenesysLoginWindow = useCallback(
    (actionAfterLogin: VoidFunction) => {
      const genesysLoginWindow = window.open(genesysTelephonyLoginUrl)
      if (!genesysLoginWindow) {
        toast({ description: t("telephony.genesys.loginWindowBlocked") })
        return
      }
      pendingGenesysLoginRef.current = { loginWindow: genesysLoginWindow, action: actionAfterLogin }
    },
    [t],
  )

  const runViaGenesys = useCallback(
    async (genesysAction: VoidFunction) => {
      const pendingGenesysLogin = pendingGenesysLoginRef.current
      if (pendingGenesysLogin && !pendingGenesysLogin.loginWindow.closed) {
        pendingGenesysLogin.loginWindow.focus()
        return
      }

      setIsGenesysLoginInFlight(true)
      try {
        if (await genesysTelephonyCheckValidity()) {
          genesysAction()
          return
        }

        openGenesysLoginWindow(genesysAction)
      } catch {
        toast({ description: t("error.500.description") })
      } finally {
        setIsGenesysLoginInFlight(false)
      }
    },
    [openGenesysLoginWindow, t],
  )

  const runViaGenesysIfEnabled = useCallback(
    async (genesysAction: VoidFunction, fallbackAction: VoidFunction) => {
      if (!isGenesysCallEnabled) {
        fallbackAction()
        return
      }

      await runViaGenesys(genesysAction)
    },
    [isGenesysCallEnabled, runViaGenesys],
  )

  return {
    isGenesysCallEnabled,
    isGenesysLoginInFlight,
    openGenesysLoginWindow,
    runViaGenesys,
    runViaGenesysIfEnabled,
  }
}

export { useGenesysTelephony }
